import { AlertTriangle, Check, Download, FileUp } from "lucide-react";
import { useRef, useState } from "react";

import { Button, ErrorBanner, InfoBanner, Modal, Spinner } from "@/components/ui";
import { toApiError } from "@/lib/api";
import { useOutletContext } from "react-router-dom";

import { useT, type Chave } from "@/lib/i18n";
import { phone as formatPhone } from "@/lib/format";
import { useImportCustomers } from "@/lib/queries";
import type { CustomerImportResult, Me } from "@/lib/types";

/** Quantos problemas listar na tela. O resto vira "e mais N". */
const VISIVEIS = 8;

/** O modelo, no idioma de quem vai preencher.
 *
 * Ele era fixo em português, e por um motivo que valia na época: o
 * cabeçalho não é rótulo de tela, é o que o parser do backend procura.
 * Só que `apps/customers/importer.py` já aceita "Name,Phone,Email" — o
 * mapa de sinônimos cobre os dois idiomas, e há teste disso. Entregar
 * "Data de Nascimento" para uma oficina de Miami virou defeito sem
 * contrapartida nenhuma.
 *
 * A data do exemplo vira junto: o mesmo 03/04/1990 é 3 de abril aqui e
 * 4 de março lá, e o exemplo precisa ser lido do jeito que o importador
 * vai ler. Ver DATE_FORMATS_BY_COUNTRY em apps/customers/importer.py. */
function baixarModelo(t: (chave: Chave) => string) {
  const modelo = [t("import.csvHeader"), t("import.csvRow1"), t("import.csvRow2")].join("\n");
  // BOM na frente: sem ele o Excel abre "Observações" como "ObservaÃ§Ãµes"
  // e o dono acha que o modelo é que está quebrado.
  const url = URL.createObjectURL(
    new Blob(["\uFEFF" + modelo], { type: "text/csv" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = t("import.templateName");
  link.click();
  // Revogar no mesmo tick cancela o download em alguns navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Importar a base de clientes de uma planilha.
 *
 * Dois passos, sempre: conferir e depois confirmar. Importação é a única
 * ação do painel que cria centenas de registros de uma vez — e a única
 * cujo desfazer seria apagar cliente em massa. Ver o arquivo antes de
 * gravar custa um clique e evita esse problema inteiro.
 */
export default function ImportCustomersModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<CustomerImportResult | null>(null);
  const [feito, setFeito] = useState<CustomerImportResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const t = useT();
  const importar = useImportCustomers();
  const erro = importar.isError ? toApiError(importar.error) : null;

  const reset = () => {
    setArquivo(null);
    setPrevia(null);
    setFeito(null);
    importar.reset();
    if (inputRef.current) inputRef.current.value = "";
  };

  const fechar = () => {
    reset();
    onClose();
  };

  const escolher = (file: File | undefined) => {
    if (!file) return;
    setArquivo(file);
    setFeito(null);
    // A conferência dispara sozinha: pedir "escolher arquivo" e depois
    // "conferir" seriam dois cliques para a mesma intenção.
    importar.mutate({ file, confirm: false }, { onSuccess: setPrevia });
  };

  const confirmar = () => {
    if (!arquivo) return;
    importar.mutate({ file: arquivo, confirm: true }, { onSuccess: setFeito });
  };

  return (
    <Modal open={open} onClose={fechar} title={t("import.title")} wide>
      {feito ? (
        <Concluido resultado={feito} onNovo={reset} onClose={fechar} />
      ) : (
        <div className="space-y-4">
          <p
            className="text-sm text-steel-muted"
            dangerouslySetInnerHTML={{ __html: t("import.intro", { needs: t("import.needs") }) }}
          />

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(e) => escolher(e.target.files?.[0])}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => inputRef.current?.click()}
              disabled={importar.isPending}
            >
              <FileUp size={16} />
              {t(arquivo ? "import.changeFile" : "import.pickFile")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => baixarModelo(t)}>
              <Download size={16} />
              {t("import.download")}
            </Button>
            {arquivo && (
              <span className="truncate text-sm text-steel-dim">{arquivo.name}</span>
            )}
          </div>

          {importar.isPending && (
            <div className="flex items-center gap-2 text-sm text-steel-muted">
              <Spinner className="size-4" />
              {t("import.reading")}
            </div>
          )}

          <ErrorBanner message={erro?.message} />

          {previa && !importar.isPending && (
            <Previa resultado={previa} onConfirmar={confirmar} onCancelar={fechar} />
          )}
        </div>
      )}
    </Modal>
  );
}

function Previa({
  resultado,
  onConfirmar,
  onCancelar,
}: {
  resultado: CustomerImportResult;
  onConfirmar: () => void;
  onCancelar: () => void;
}) {
  const t = useT();
  const { region } = useOutletContext<Me>().garage;
  const semCabecalho = Object.keys(resultado.header).length === 0;
  const problemas = [...resultado.errors, ...resultado.warnings].sort((a, b) => a.line - b.line);

  return (
    <div className="space-y-4 border-t border-line pt-4">
      <div className="grid grid-cols-3 gap-2 text-center">
        <Numero valor={resultado.new} rotulo={t("import.toImport")} destaque />
        <Numero valor={resultado.existing} rotulo={t("modal.alreadyThere")} />
        <Numero valor={resultado.invalid} rotulo={t("modal.withProblems")} />
      </div>

      {semCabecalho && (
        <InfoBanner>
          <span
            dangerouslySetInnerHTML={{
              __html: t("import.noHeader", { columns: t("import.columns") }),
            }}
          />
        </InfoBanner>
      )}

      {resultado.truncated && (
        <InfoBanner tone="neutral">{t("import.truncated")}</InfoBanner>
      )}

      {resultado.existing > 0 && (
        <p className="text-xs text-steel-dim">{t("import.skipped")}</p>
      )}

      {resultado.sample.length > 0 && (
        <div>
          <span className="eyebrow mb-2 block text-steel-dim">
            {t("import.sample")}
          </span>
          <div className="overflow-x-auto rounded-[4px] border border-line">
            <table className="w-full text-sm">
              <tbody>
                {resultado.sample.map((c) => (
                  <tr key={c.line} className="border-b border-line/60 last:border-0">
                    <td className="px-3 py-2 font-medium">{c.name}</td>
                    <td className="px-3 py-2 text-steel-muted">{formatPhone(c.phone, region.country)}</td>
                    <td className="px-3 py-2 text-steel-dim">{c.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {problemas.length > 0 && (
        <div>
          <span className="eyebrow mb-2 flex items-center gap-1.5 text-amber-400">
            <AlertTriangle size={14} />
            {t("import.problems")}
          </span>
          <ul className="space-y-1 text-sm text-steel-muted">
            {problemas.slice(0, VISIVEIS).map((p, i) => (
              <li key={`${p.line}-${i}`}>
                <span className="text-steel-dim">{t("import.row", { n: p.line })}</span> {p.reason}
              </li>
            ))}
          </ul>
          {problemas.length > VISIVEIS && (
            <p className="mt-1 text-xs text-steel-dim">
              {t("import.andMore", { n: problemas.length - VISIVEIS })}
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={onCancelar}>
          {t("common.cancel")}
        </Button>
        <Button
          type="button"
          variant="racing"
          className="flex-1"
          disabled={resultado.new === 0}
          onClick={onConfirmar}
        >
          {resultado.new === 0
            ? t("modal.nothingNew")
            : t("import.importN", { n: resultado.new })}
        </Button>
      </div>
    </div>
  );
}

function Concluido({
  resultado,
  onNovo,
  onClose,
}: {
  resultado: CustomerImportResult;
  onNovo: () => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <div className="space-y-4 text-center">
      <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-emerald-950/60 text-emerald-400">
        <Check size={24} />
      </div>
      <div>
        <h3 className="font-display text-lg font-bold">
          {t("import.imported", { n: resultado.imported })}
        </h3>
        <p className="mt-1 text-sm text-steel-muted">
          {resultado.existing > 0 && t("import.alreadyHad", { n: resultado.existing })}
          {resultado.invalid > 0 &&
            t("import.leftOut", { n: resultado.invalid })}
        </p>
      </div>
      <div className="flex justify-center gap-2">
        <Button type="button" variant="secondary" onClick={onNovo}>
          {t("import.another")}
        </Button>
        <Button type="button" variant="racing" onClick={onClose}>
          {t("common.done")}
        </Button>
      </div>
    </div>
  );
}

function Numero({
  valor,
  rotulo,
  destaque,
}: {
  valor: number;
  rotulo: string;
  destaque?: boolean;
}) {
  return (
    <div className="rounded-[4px] border border-line px-2 py-3">
      <p className={destaque ? "text-2xl font-bold text-racing-500" : "text-2xl font-bold"}>
        {valor}
      </p>
      <p className="text-xs text-steel-dim">{rotulo}</p>
    </div>
  );
}
